import { Badge } from "./Badge";
import { formatCountMap } from "../lib/format";
import { ApiRequestError } from "../types";
import type { GhlStatus } from "../types";

interface GhlAuthPanelProps {
  status: GhlStatus | undefined;
  isLoading: boolean;
  error: unknown;
}

function sumByMode(rows: GhlStatus["sites"] | undefined, mode: string): number {
  return (rows ?? []).filter((r) => r.auth_mode === mode).reduce((s, r) => s + (r.count || 0), 0);
}

export function GhlAuthPanel({ status, isLoading, error }: GhlAuthPanelProps) {
  const oauthOk = Boolean(status?.oauth_configured);
  const oauthSites = sumByMode(status?.sites, "oauth_managed");
  const fallbackSites = sumByMode(status?.sites, "fallback_pit");
  const resolver = status?.resolver;
  const credentialCount = (status?.credentials ?? []).reduce(
    (sum, row) => sum + (Number(row.count) || 0),
    0,
  );
  return (
    <article className="panel" aria-labelledby="ghlPanelTitle">
      <div className="panel-header">
        <h2 id="ghlPanelTitle">GHL Auth</h2>
        <Badge tone={isLoading ? "" : error ? "bad" : oauthOk ? "ok" : "warn"}>
          {isLoading
            ? "Loading"
            : error
              ? "Failed"
              : oauthOk
                ? "OAuth configured"
                : "OAuth missing"}
        </Badge>
      </div>
      {error ? (
        <p className="panel-error">
          {error instanceof ApiRequestError
            ? `${error.endpoint} failed: ${error.message}`
            : `Request failed: ${(error as Error).message ?? "unknown error"}`}
        </p>
      ) : null}
      <dl className="facts">
        <dt>OAuth config</dt>
        <dd className={status ? (oauthOk ? "value-ok" : "value-warn") : "muted"}>
          {status ? (oauthOk ? "Client id + secret present" : "Missing client id or secret") : "…"}
        </dd>
        <dt>Installs</dt>
        <dd>{status ? formatCountMap(status.installations) : "…"}</dd>
        <dt>OAuth-managed sites</dt>
        <dd className={oauthSites > 0 ? "value-ok" : "muted"}>{status ? oauthSites : "…"}</dd>
        <dt>OAuth write gate</dt>
        <dd className={resolver?.oauth_write_ready_sites ? "value-ok" : "value-warn"}>
          {resolver
            ? resolver.oauth_write_ready_sites > 0
              ? `${resolver.oauth_write_ready_sites} write-ready`
              : resolver.zero_touch_gate === "not_configured"
                ? "OAuth config missing"
                : "Location-token resolver unproven"
            : "…"}
        </dd>
        <dt>Fallback sites</dt>
        <dd className={fallbackSites > 0 ? "value-warn" : "muted"}>
          {status ? fallbackSites : "…"}
        </dd>
        <dt>Workflow write-ready</dt>
        <dd className={resolver?.write_ready_sites ? "value-ok" : "value-warn"}>
          {resolver ? `${resolver.write_ready_sites} site row(s)` : "…"}
        </dd>
        <dt>Credential rows</dt>
        <dd className="muted">
          {status ? (credentialCount === 0 ? "0" : `${credentialCount} metadata row(s)`) : "…"}
        </dd>
      </dl>
    </article>
  );
}
